import React, { useState, useMemo } from 'react'
import dayjs from 'dayjs'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2,
  useTheme,
  useMediaQuery,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import CalendarToday from '@mui/icons-material/CalendarToday'
import LocalHospital from '@mui/icons-material/LocalHospital'
import Emergency from '@mui/icons-material/Emergency'
import TrendingUp from '@mui/icons-material/TrendingUp'
import Business from '@mui/icons-material/Business'
import Groups from '@mui/icons-material/Groups'
import Summarize from '@mui/icons-material/Summarize'
import Download from '@mui/icons-material/Download'
import { toast } from 'react-toastify'
import { sendDaySummaryToDoctor } from 'src/consumer/services/featureServices'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { VISIT_EVENT_TYPES } from 'src/consumer/constants'
import {
  getFeeCollectedFromBookedSlotsWithCurrencyString,
  getFilteredBookedSlotsByVisitEventType,
} from './utils'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getIpAddressFromLocalStorage } from 'src/consumer/services/localstorageServices'
import { getIpAddress } from 'src/helper'
import { getMPData } from 'src/mentor/services'
import jsPDF from 'jspdf'
import styled from 'styled-components'

// Helper function to detect if text contains Hindi/Devanagari characters
function containsHindi(text) {
  if (!text) return false
  // Devanagari Unicode range: U+0900 to U+097F
  return /[\u0900-\u097F]/.test(text)
}

// Helper function to split text into lines that fit within width, handling Hindi characters
function splitTextForPDF(doc, text, maxWidth) {
  if (!text) return ['']
  
  // For Hindi text, we need to be more careful with splitting
  // jsPDF doesn't handle Hindi well, so we'll try to preserve words
  const words = text.split(/\s+/)
  const lines = []
  let currentLine = ''
  
  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const textWidth = doc.getTextWidth(testLine)
    
    if (textWidth > maxWidth && currentLine) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = testLine
    }
  })
  
  if (currentLine) {
    lines.push(currentLine)
  }
  
  return lines.length > 0 ? lines : [text]
}

const SummarySection = styled(Box)`
  margin-top: 32px;
  margin-bottom: 24px;
  padding: 24px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%);
  border-radius: 16px;
  border: 1px solid rgba(67, 56, 202, 0.1);
  box-shadow: 
    0 4px 16px rgba(67, 56, 202, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.04);
  
  @media (max-width: 768px) {
    padding: 16px;
    margin-top: 24px;
    margin-bottom: 16px;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    margin-top: 16px;
    margin-bottom: 12px;
    border-radius: 10px;
  }
`

const SectionHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid rgba(67, 56, 202, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
    padding-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 10px;
    margin-bottom: 14px;
    padding-bottom: 10px;
  }
`

const SectionTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 700;
  color: rgba(67, 56, 202, 0.9);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 15px;
  }
`

const SectionSubtitle = styled(Typography)`
  font-size: 13px;
  font-weight: 500;
  color: rgba(67, 56, 202, 0.65);
  margin-top: 4px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
    margin-top: 2px;
  }
`

const StatCard = styled(Card)`
  height: 100%;
  border-radius: 18px;
  border: 2px solid ${props => props.$borderColor || 'rgba(67, 56, 202, 0.15)'};
  background: ${props => props.$background || 'rgba(255, 255, 255, 0.95)'};
  box-shadow: 
    0 2px 8px rgba(67, 56, 202, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 4px 16px rgba(67, 56, 202, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.08);
    border-color: ${props => props.$borderColor || 'rgba(67, 56, 202, 0.3)'};
  }
  
  @media (max-width: 768px) {
    border-radius: 16px;
  }
  
  @media (max-width: 480px) {
    border-radius: 12px;
    border-width: 1.5px;
  }
`

const StatCardContent = styled(CardContent)`
  padding: 20px !important;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  @media (max-width: 768px) {
    padding: 16px !important;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 12px !important;
    gap: 6px;
  }
`

const StatHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`

const StatIcon = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$bgColor || 'rgba(67, 56, 202, 0.1)'};
  color: ${props => props.$iconColor || 'rgba(67, 56, 202, 0.8)'};
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }
  
  svg {
    font-size: 20px;
    
    @media (max-width: 768px) {
      font-size: 18px;
    }
    
    @media (max-width: 480px) {
      font-size: 16px;
    }
  }
`

const StatLabel = styled(Typography)`
  font-size: 13px;
  font-weight: 600;
  color: rgba(67, 56, 202, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
    letter-spacing: 0.3px;
  }
`

const StatValue = styled(Typography)`
  font-size: 24px;
  font-weight: 700;
  color: rgba(67, 56, 202, 0.95);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
  }
`

const StatAmount = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  color: rgba(67, 56, 202, 0.85);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`

const TotalCard = styled(Card)`
  height: 100%;
  border-radius: 18px;
  border: 3px solid rgba(67, 56, 202, 0.3);
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%);
  box-shadow: 
    0 4px 16px rgba(67, 56, 202, 0.15),
    0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 
      0 6px 20px rgba(67, 56, 202, 0.2),
      0 3px 10px rgba(0, 0, 0, 0.1);
    border-color: rgba(67, 56, 202, 0.4);
  }
  
  @media (max-width: 768px) {
    border-radius: 16px;
  }
  
  @media (max-width: 480px) {
    border-radius: 12px;
    border-width: 2px;
  }
`

const FilterPillsContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 12px;
    gap: 4px;
  }
`

const FilterPill = styled(Chip)`
  height: 32px;
  font-size: 12px;
  font-weight: ${props => props.$selected ? '600' : '500'};
  border: 2px solid ${props => props.$selected 
    ? 'rgba(67, 56, 202, 0.4)' 
    : 'rgba(67, 56, 202, 0.15)'};
  background: ${props => props.$selected 
    ? 'linear-gradient(135deg, rgba(67, 56, 202, 0.12) 0%, rgba(99, 102, 241, 0.12) 100%)' 
    : 'rgba(255, 255, 255, 0.9)'};
  color: ${props => props.$selected 
    ? 'rgba(67, 56, 202, 0.95)' 
    : 'rgba(67, 56, 202, 0.7)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  &:hover {
    background: ${props => props.$selected 
      ? 'linear-gradient(135deg, rgba(67, 56, 202, 0.18) 0%, rgba(99, 102, 241, 0.18) 100%)' 
      : 'rgba(67, 56, 202, 0.06)'};
    border-color: ${props => props.$selected 
      ? 'rgba(67, 56, 202, 0.5)' 
      : 'rgba(67, 56, 202, 0.25)'};
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    height: 28px;
    font-size: 11px;
  }
  
  @media (max-width: 480px) {
    height: 26px;
    font-size: 10px;
    border-width: 1.5px;
  }
  
  svg {
    font-size: 14px;
    margin-left: 4px;
    color: ${props => props.$selected 
      ? 'rgba(67, 56, 202, 0.95)' 
      : 'rgba(67, 56, 202, 0.7)'} !important;
    
    @media (max-width: 768px) {
      font-size: 12px;
    }
    
    @media (max-width: 480px) {
      font-size: 11px;
      margin-left: 2px;
    }
  }
  
  .MuiChip-icon {
    color: ${props => props.$selected 
      ? 'rgba(67, 56, 202, 0.95)' 
      : 'rgba(67, 56, 202, 0.7)'} !important;
  }
  
  .MuiChip-label {
    color: ${props => props.$selected 
      ? 'rgba(67, 56, 202, 0.95)' 
      : 'rgba(67, 56, 202, 0.7)'};
  }
`

export default function DayViewFeeComponent({ 
  bookedSlots, 
  currentClinicData, 
  clinicId,
  selectedDate,
  forMentor = false,
  mentorProfileData,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [filterType, setFilterType] = useState(currentClinicData ? 'current' : 'all')
  const [sendReportLoading, setSendReportLoading] = useState(false)
  const [downloadPdfLoading, setDownloadPdfLoading] = useState(false)
  
  // Calculate UTC times for the selected date
  const selectedDateUtcStartTime = useMemo(() => {
    if (!selectedDate) return null
    const date = dayjs(selectedDate, 'DD-MM-YYYY').startOf('day')
    return date.toISOString()
  }, [selectedDate])
  
  const selectedDateUtcEndTime = useMemo(() => {
    if (!selectedDate) return null
    const date = dayjs(selectedDate, 'DD-MM-YYYY').endOf('day')
    return date.toISOString()
  }, [selectedDate])
  
  async function sendDayReportForDoctor() {
    if (!selectedDateUtcStartTime || !selectedDateUtcEndTime) {
      toast.error('Invalid date selected')
      return
    }
    setSendReportLoading(true)
    const response = await sendDaySummaryToDoctor(selectedDateUtcStartTime, selectedDateUtcEndTime)
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      toast.success("Day's summary sent to your preferred communication mediums.")
    } else {
      toast.error('Error sending summary to your preferred communication mediums')
    }
    setSendReportLoading(false)
  }
  
  // Role and visibility checks
  const roles = getAllRolesInBoolean()
  const isSupportPortal = typeof window !== 'undefined' && window.location?.pathname?.includes('/support')
  const currentPath = typeof window !== 'undefined' ? window.location?.pathname : ''
  
  // Check user roles
  const getUserRoles = () => {
    try {
      return localStorage.getItem('authorities') || ''
    } catch (error) {
      return ''
    }
  }
  const userRoles = getUserRoles()
  const hasFinanceRole = userRoles?.includes('ROLE_FINANCE')
  const hasTeacherRole = userRoles?.includes('ROLE_TEACHER')
  const hasDoctorRole = userRoles?.includes('ROLE_DOCTOR')
  const hasTherapistRole = userRoles?.includes('ROLE_THERAPIST')
  
  // Check if user is Clinic Owner (Doctor who is Clinic Owner)
  const isClinicOwnerDoctor = useMemo(() => {
    return roles?.isClinicOwner && roles?.isDoctor
  }, [roles])

  // Determine visibility
  const shouldShowComponent = useMemo(() => {
    // Support Portal: ROLE_FINANCE
    if (isSupportPortal && hasFinanceRole) {
      return true
    }
    
    // Mentor Portal: ROLE_TEACHER (includes ROLE_DOCTOR, ROLE_THERAPIST)
    if (!isSupportPortal && (hasTeacherRole || hasDoctorRole || hasTherapistRole)) {
      return true
    }
    
    // ROLE_THERAPY_SUP_L3: Only for therapists URL
    if (roles?.isL3TherapySupport && currentPath?.includes('/support-home/practitioner-management/therapists/')) {
      return true
    }
    
    // ROLE_CLINIC_SUCCESS_MANAGER: Only for doctors URL
    if (roles?.isClinicSuccessManager && currentPath?.includes('/support-home/practitioner-management/doctors/')) {
      return true
    }
    
    return false
  }, [isSupportPortal, hasFinanceRole, hasTeacherRole, hasDoctorRole, hasTherapistRole, roles, currentPath])
  
  // Determine if monetary values should be hidden
  const hideMonetaryValues = useMemo(() => {
    return (roles?.isL3TherapySupport || roles?.isClinicSuccessManager) && isSupportPortal
  }, [roles, isSupportPortal])
  
  // Don't render if not visible
  if (!shouldShowComponent) {
    return null
  }
  
  // Determine which clinic ID to use based on filter
  const activeClinicId = useMemo(() => {
    if (filterType === 'all') {
      return null // Show all clinics
    }
    return currentClinicData?.id || clinicId
  }, [filterType, currentClinicData, clinicId])

  const regularBookedSlots = getFilteredBookedSlotsByVisitEventType(
    bookedSlots,
    VISIT_EVENT_TYPES.regular,
    activeClinicId,
  )
  const followUpBookedSlots = getFilteredBookedSlotsByVisitEventType(
    bookedSlots,
    VISIT_EVENT_TYPES.followUp,
    activeClinicId,
  )
  const emergencyBookedSlots = getFilteredBookedSlotsByVisitEventType(
    bookedSlots,
    VISIT_EVENT_TYPES.emergency,
    activeClinicId,
  )

  const allSlots = getFilteredBookedSlotsByVisitEventType(bookedSlots, undefined, activeClinicId)

  const regularCount = regularBookedSlots?.length || 0
  const followUpCount = followUpBookedSlots?.length || 0
  const emergencyCount = emergencyBookedSlots?.length || 0
  const totalCount = allSlots?.length || 0

  const regularFee = getFeeCollectedFromBookedSlotsWithCurrencyString(regularBookedSlots)
  const followUpFee = getFeeCollectedFromBookedSlotsWithCurrencyString(followUpBookedSlots)
  const emergencyFee = getFeeCollectedFromBookedSlotsWithCurrencyString(emergencyBookedSlots)
  const totalFee = getFeeCollectedFromBookedSlotsWithCurrencyString(allSlots)

  // PDF Download Function
  async function downloadPatientDetailsPDF() {
    if (!allSlots || allSlots.length === 0) {
      toast.error('No patient data available to download')
      return
    }

    setDownloadPdfLoading(true)
    try {
      // Get IP address
      let ipAddress = getIpAddressFromLocalStorage()
      if (!ipAddress) {
        // getIpAddress returns null on error, so no need for try-catch
        ipAddress = await getIpAddress() || 'N/A'
      }

      // Get user name - contextual: Mentor Portal uses mentorProfileData.name, fallback to user.name
      let userName = mentorProfileData?.name || mentorProfileData?.firstName
      
      // If not available from mentor profile, fetch from user profile
      if (!userName) {
        try {
          const userProfileResp = await getMPData('/api/secure/user/profile')
          if (userProfileResp?.status === 200) {
            userName = userProfileResp?.data?.name || userProfileResp?.data?.firstName
          }
        } catch (error) {
          console.error('Error fetching user profile:', error)
        }
      }
      
      // Final fallback
      userName = userName || localStorage.getItem('userName') || 'Unknown User'

      // Get clinic brand and location names
      const clinicBrandName = currentClinicData?.clinicBrandName || 
                             currentClinicData?.clinicBrandData?.name || 
                             allSlots[0]?.clinicBrandData?.name || 
                             allSlots[0]?.clinicBrandName || 
                             'N/A'
      
      const clinicLocationName = currentClinicData?.title || 
                                currentClinicData?.name || 
                                currentClinicData?.clinicLocationName ||
                                allSlots[0]?.clinicLocationData?.title || 
                                allSlots[0]?.clinicLocationData?.name ||
                                allSlots[0]?.clinicName ||
                                allSlots[0]?.clinicLocationName ||
                                'N/A'

      // Create PDF
      const doc = new jsPDF()
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 20
      const lineHeight = 7
      let yPosition = margin

      // Header
      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(67, 56, 202)
      doc.text(clinicBrandName, margin, yPosition)
      yPosition += lineHeight + 2

      doc.setFontSize(14)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(0, 0, 0)
      doc.text(clinicLocationName, margin, yPosition)
      yPosition += lineHeight + 8

      // Date
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text(`Date: ${selectedDate ? dayjs(selectedDate, 'DD-MM-YYYY').format('DD MMMM YYYY') : dayjs().format('DD MMMM YYYY')}`, margin, yPosition)
      yPosition += lineHeight + 8

      // Table Header
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(255, 255, 255)
      doc.setFillColor(67, 56, 202)
      doc.rect(margin, yPosition - 5, pageWidth - 2 * margin, 8, 'F')
      
      const colWidths = [25, 50, 25, 30, 50] // Patient Number, Name, Age/Gender, City, Contact
      const headers = ['Patient #', 'Name', 'Age/Gender', 'City', 'Contact']
      let xPosition = margin + 2
      
      headers.forEach((header, index) => {
        doc.text(header, xPosition, yPosition)
        xPosition += colWidths[index]
      })
      yPosition += lineHeight + 5

      // Table Data
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(0, 0, 0)
      
      allSlots.forEach((slot, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 30) {
          doc.addPage()
          yPosition = margin
        }

        const patientInfo = slot?.patientInfo
        const patientNumber = patientInfo?.patientNumber || 'N/A'
        const patientName = slot?.userName || patientInfo?.name || 'N/A'
        const age = patientInfo?.dateOfBirth ? getAgeFromDobWithTime(patientInfo.dateOfBirth) : 'N/A'
        const gender = patientInfo?.gender ? patientInfo.gender.charAt(0).toUpperCase() : 'N/A'
        const ageGender = `${age}${age !== 'N/A' ? 'Y' : ''}/${gender}`
        const city = patientInfo?.city || patientInfo?.location?.city || 'N/A'
        const contact = patientInfo?.phoneNumber || patientInfo?.email || 'N/A'

        const rowData = [patientNumber, patientName, ageGender, city, contact]
        
        // First pass: Calculate max height needed for this row (for multi-line Hindi text)
        let maxRowHeight = lineHeight
        const rowTexts = rowData.map((data, colIndex) => {
          let text = String(data || 'N/A')
          const maxWidth = colWidths[colIndex] - 2
          
          if (containsHindi(text)) {
            const lines = splitTextForPDF(doc, text, maxWidth)
            const columnHeight = lines.length * lineHeight
            if (columnHeight > maxRowHeight) {
              maxRowHeight = columnHeight
            }
            return { text, lines, isHindi: true, maxWidth }
          } else {
            // For non-Hindi text, truncate if too long
            const textWidth = doc.getTextWidth(text)
            if (textWidth > maxWidth) {
              let truncated = text
              while (doc.getTextWidth(truncated + '...') > maxWidth && truncated.length > 0) {
                truncated = truncated.slice(0, -1)
              }
              text = truncated + (truncated.length < text.length ? '...' : '')
            }
            return { text, isHindi: false, maxWidth }
          }
        })
        
        // Draw alternate row background (after calculating height)
        if (index % 2 === 0) {
          doc.setFillColor(249, 250, 251)
          doc.rect(margin, yPosition - 5, pageWidth - 2 * margin, maxRowHeight + 2, 'F')
        }
        
        // Second pass: Render all columns at the same yPosition
        let xPos = margin + 2
        rowTexts.forEach((textData, colIndex) => {
          if (textData.isHindi) {
            // Render Hindi text line by line, aligned to top of row
            textData.lines.forEach((line, lineIndex) => {
              const lineY = yPosition + (lineIndex * lineHeight)
              // Check if we need a new page
              if (lineY > pageHeight - 30) {
                doc.addPage()
                yPosition = margin
                const newLineY = yPosition + (lineIndex * lineHeight)
                try {
                  doc.text(line, xPos, newLineY)
                } catch (error) {
                  console.warn('Error rendering Hindi text:', error)
                  doc.text(line, xPos, newLineY)
                }
              } else {
                try {
                  doc.text(line, xPos, lineY)
                } catch (error) {
                  console.warn('Error rendering Hindi text:', error)
                  doc.text(line, xPos, lineY)
                }
              }
            })
          } else {
            // Render non-Hindi text at the top of the row
            doc.text(textData.text, xPos, yPosition)
          }
          
          xPos += colWidths[colIndex]
        })
        
        // Move to next row, accounting for multi-line content
        yPosition += maxRowHeight + 2
      })

      // Footer
      const footerY = pageHeight - 20
      doc.setFontSize(8)
      doc.setFont('helvetica', 'italic')
      doc.setTextColor(100, 100, 100)
      
      const generatedTime = dayjs().format('DD MMM YYYY, HH:mm:ss')
      const footerText = `Confidential | Generated by: ${userName} | Generated on: ${generatedTime} | Generated from IP: ${ipAddress}`
      
      doc.text(footerText, margin, footerY)
      doc.text('This document contains confidential patient information and should be handled securely.', margin, footerY + 5)

      // Save PDF
      const fileName = `Patient_Details_${selectedDate ? dayjs(selectedDate, 'DD-MM-YYYY').format('DD-MMM-YYYY') : dayjs().format('DD-MMM-YYYY')}.pdf`
      doc.save(fileName)
      
      toast.success('PDF downloaded successfully')
    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error('Error generating PDF. Please try again.')
    } finally {
      setDownloadPdfLoading(false)
    }
  }

  return (
    <SummarySection>
      <SectionHeader>
        <Box sx={{ flex: 1 }}>
          <SectionTitle>
            Day Summary
          </SectionTitle>
          <SectionSubtitle>
            {selectedDate ? dayjs(selectedDate, 'DD-MM-YYYY').format('DD MMM YYYY') : dayjs().format('DD MMM YYYY')}
          </SectionSubtitle>
        </Box>
        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          alignItems: 'center',
          width: { xs: '100%', md: 'auto' },
          justifyContent: { xs: 'flex-start', md: 'flex-end' },
          flexWrap: { xs: 'wrap', md: 'nowrap' }
        }}>
          {/* Download PDF Button - Only for Clinic Owner Doctors */}
          {isClinicOwnerDoctor && (
            <Tooltip title="Download patient details as PDF">
              <span>
                <IconButton
                  size={isMobile ? 'small' : 'medium'}
                  onClick={downloadPatientDetailsPDF}
                  disabled={downloadPdfLoading || !allSlots || allSlots.length === 0}
                  sx={{
                  color: 'rgba(67, 56, 202, 0.85)',
                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                  border: '1.5px solid rgba(67, 56, 202, 0.2)',
                  padding: { xs: '8px', md: '10px' },
                  '&:hover': {
                    backgroundColor: 'rgba(67, 56, 202, 0.15)',
                    borderColor: 'rgba(67, 56, 202, 0.35)',
                    color: 'rgba(67, 56, 202, 1)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.2)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                  {downloadPdfLoading ? (
                    <CircularProgress size={isMobile ? 18 : 20} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
                  ) : (
                    <Download sx={{ fontSize: { xs: 20, md: 22 } }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Get Day's Summary Button */}
          {forMentor &&
            !(
              getAllRolesInBoolean().isL1TherapySupport ||
              getAllRolesInBoolean().isL2TherapySupport ||
              getAllRolesInBoolean().isL3TherapySupport ||
              getAllRolesInBoolean().isClinicSuccessManager
            ) && (
              <Tooltip title="Get day's summary on your preferred communication mediums">
                <span>
                  <IconButton
                    size={isMobile ? 'small' : 'medium'}
                    onClick={sendDayReportForDoctor}
                    disabled={sendReportLoading || !selectedDateUtcStartTime || !selectedDateUtcEndTime}
                    sx={{
                      color: 'rgba(67, 56, 202, 0.85)',
                      backgroundColor: 'rgba(67, 56, 202, 0.08)',
                      border: '1.5px solid rgba(67, 56, 202, 0.2)',
                      padding: { xs: '8px', md: '10px' },
                      '&:hover': {
                        backgroundColor: 'rgba(67, 56, 202, 0.15)',
                        borderColor: 'rgba(67, 56, 202, 0.35)',
                        color: 'rgba(67, 56, 202, 1)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(67, 56, 202, 0.2)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        borderColor: 'rgba(0, 0, 0, 0.12)',
                        color: 'rgba(0, 0, 0, 0.26)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {sendReportLoading ? (
                      <CircularProgress size={isMobile ? 18 : 20} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
                    ) : (
                      <Summarize sx={{ fontSize: { xs: 20, md: 22 } }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
        </Box>
      </SectionHeader>

      {/* Filter Pills */}
      <FilterPillsContainer>
        <FilterPill
          icon={<Groups sx={{ fontSize: { xs: 12, md: 14 } }} />}
          label="All Clinics"
          $selected={filterType === 'all'}
          onClick={() => setFilterType('all')}
          clickable
        />
        {currentClinicData && (
          <FilterPill
            icon={<Business sx={{ fontSize: { xs: 12, md: 14 } }} />}
            label={currentClinicData?.title || 'Current Clinic'}
            $selected={filterType === 'current'}
            onClick={() => setFilterType('current')}
            clickable
          />
        )}
      </FilterPillsContainer>

      <Grid2 container spacing={isMobile ? 1.5 : 3}>
        {/* Regular Bookings */}
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <StatCard 
            $borderColor="rgba(67, 56, 202, 0.2)"
            $background="rgba(255, 255, 255, 0.95)"
          >
            <StatCardContent>
              <StatHeader>
                <StatIcon $bgColor="rgba(67, 56, 202, 0.1)" $iconColor="rgba(67, 56, 202, 0.8)">
                  <CalendarToday />
                </StatIcon>
                <StatLabel>Regular</StatLabel>
              </StatHeader>
              <StatValue>{regularCount}</StatValue>
              {!hideMonetaryValues && <StatAmount>{regularFee}</StatAmount>}
            </StatCardContent>
          </StatCard>
        </Grid2>

        {/* Follow Up Bookings */}
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <StatCard 
            $borderColor="rgba(99, 102, 241, 0.2)"
            $background="rgba(255, 255, 255, 0.95)"
          >
            <StatCardContent>
              <StatHeader>
                <StatIcon $bgColor="rgba(99, 102, 241, 0.1)" $iconColor="rgba(99, 102, 241, 0.8)">
                  <TrendingUp />
                </StatIcon>
                <StatLabel>Follow Up</StatLabel>
              </StatHeader>
              <StatValue>{followUpCount}</StatValue>
              {!hideMonetaryValues && <StatAmount>{followUpFee}</StatAmount>}
            </StatCardContent>
          </StatCard>
        </Grid2>

        {/* Emergency Bookings */}
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <StatCard 
            $borderColor="rgba(239, 68, 68, 0.2)"
            $background="rgba(255, 255, 255, 0.95)"
          >
            <StatCardContent>
              <StatHeader>
                <StatIcon $bgColor="rgba(239, 68, 68, 0.1)" $iconColor="rgba(239, 68, 68, 0.8)">
                  <Emergency />
                </StatIcon>
                <StatLabel>Emergency</StatLabel>
              </StatHeader>
              <StatValue>{emergencyCount}</StatValue>
              {!hideMonetaryValues && <StatAmount>{emergencyFee}</StatAmount>}
            </StatCardContent>
          </StatCard>
        </Grid2>

        {/* Total */}
        <Grid2 size={{ xs: 6, sm: 3 }}>
          <TotalCard>
            <StatCardContent>
              <StatHeader>
                <StatIcon $bgColor="rgba(67, 56, 202, 0.2)" $iconColor="rgba(67, 56, 202, 1)">
                  <LocalHospital />
                </StatIcon>
                <StatLabel>Total</StatLabel>
              </StatHeader>
              <StatValue>{totalCount}</StatValue>
              {!hideMonetaryValues && (
                <StatAmount sx={{ color: 'rgba(67, 56, 202, 1)', fontWeight: 700 }}>
                  {totalFee}
                </StatAmount>
              )}
            </StatCardContent>
          </TotalCard>
        </Grid2>
      </Grid2>
    </SummarySection>
  )
}
